import React, { useState, useEffect } from 'react';
import styles from './RoleManagement.module.css';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
// import ConfirmationModal from '../componen';

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'OK',
        cancelText: 'Cancel',
        onConfirm: null,
        onCancel: null
    });

    const [formData, setFormData] = useState({
        roleName: '',
        description: ''
    });

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        fetchRoles();
    }, [searchTerm, currentPage, rowsPerPage]);

    const fetchRoles = async () => {
        try {
            const response = await fetch('https://hms-render.onrender.com/api/roles/list', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    page: currentPage,
                    limit: rowsPerPage,
                    search: searchTerm
                })
            });

            const data = await response.json();

            if (data.success) {
                const mappedRoles = data.data.map(role => ({
                    id: role.role_id,
                    name: role.role_name,
                    description: role.role_description,
                    status: role.status,
                    userCount: role.user_count,
                    createdAt: role.created_at,
                    updatedAt: role.updated_at,
                    createdByName: role.created_by_name,
                    updatedByName: role.updated_by_name
                }));

                setRoles(mappedRoles);
                setTotalPages(data.pagination.totalPages);
                setTotalRecords(data.pagination.totalRecords);
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: 'Failed to fetch roles',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('  Error fetching roles:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'Failed to fetch roles',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleEdit = (role) => {
        setSelectedRole(role);
        setFormData({
            roleName: role.name,
            description: role.description || ''
        });
        setShowEditModal(true);
    };

    const handleDelete = (role) => {
        setConfirmModal({
            show: true,
            title: 'Delete Role',
            message: `Are you sure you want to delete role "${role.name}"? This action cannot be undone.`,
            type: 'delete',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: () => {
                setConfirmModal({ ...confirmModal, show: false });
                performDelete(role);
            },
            onCancel: () => setConfirmModal({ ...confirmModal, show: false })
        });
    };

    const performDelete = async (role) => {
        try {
            const response = await fetch(`https://hms-render.onrender.com/api/roles/${role.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    deleted_by: currentUser.userId
                })
            });

            const data = await response.json();

            if (data.success) {
                fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Deleted!',
                    message: 'Role deleted successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to delete role',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('Delete error:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'Error: ' + error.message,
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleHistory = (role) => {
        setSelectedRole(role);
        setShowHistoryModal(true);
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('https://hms-render.onrender.com/api/roles/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    role_name: formData.roleName,
                    role_description: formData.description,
                    created_by: currentUser.userId
                })
            });

            const data = await response.json();

            if (data.success) {
                setShowCreateModal(false);
                setFormData({ roleName: '', description: '' });
                fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'Role created successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to create role',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('Error creating role:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'An error occurred while creating role',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(`https://hms-render.onrender.com/api/roles/${selectedRole.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    role_name: formData.roleName,
                    role_description: formData.description,
                    updated_by: currentUser.userId
                })
            });

            const data = await response.json();

            if (data.success) {
                setShowEditModal(false);
                setFormData({ roleName: '', description: '' });
                fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'Role updated successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to update role',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('Error updating role:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'An error occurred while updating role',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleExport = () => {
        setConfirmModal({
            show: true,
            title: 'Coming Soon',
            message: 'Export functionality will be implemented soon.',
            type: 'info',
            onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Role Management</h1>
                <button className={styles.createButton} onClick={() => setShowCreateModal(true)}>
                    <span className={styles.buttonIcon}>+</span>
                    Create Role
                </button>
            </div>

            <div className={styles.searchBar}>
                <div className={styles.searchWrapper}>
                    <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search roles..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
                <div className={styles.actionButtons}>
                    <button className={styles.actionButton}>
                        <span className={styles.actionIcon}>⚙</span>
                        Customize
                    </button>
                    <button className={styles.actionButton} onClick={handleExport}>
                        <span className={styles.actionIcon}>↓</span>
                        Export
                    </button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.tableHead}>
                        <tr>
                            <th>Role Name <button className={styles.sortButton}>↕</button></th>
                            <th>Status <button className={styles.sortButton}>↕</button></th>
                            <th className={styles.actionsColumn}>Actions</th>
                        </tr>
                    </thead>
                    <tbody className={styles.tableBody}>
                        {roles.length > 0 ? (
                            roles.map((role) => (
                                <tr key={role.id} className={styles.tableRow}>
                                    <td>
                                        <div className={styles.roleCell}>
                                            <div className={styles.roleName}>{role.name}</div>
                                            {role.description && (
                                                <div className={styles.roleDescription}>{role.description}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${role.status === 'Active' ? styles.statusActive : styles.statusInactive}`}>
                                            {role.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.actionIcons}>
                                            <button className={styles.iconButton} onClick={() => handleEdit(role)} title="Edit">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </button>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => handleDelete(role)}
                                                title="Delete"
                                                disabled={role.userCount > 0}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                </svg>
                                            </button>
                                            <button className={styles.iconButton} onClick={() => handleHistory(role)} title="History">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className={styles.noData}>No roles found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={styles.pagination}>
                <div className={styles.rowsPerPage}>
                    <span>Rows per page</span>
                    <select className={styles.rowsSelect} value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                <div className={styles.pageInfo}>
                    Showing {roles.length > 0 ? ((currentPage - 1) * rowsPerPage + 1) : 0} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} results
                </div>
                <div className={styles.pageControls}>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>««</button>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>‹</button>
                    <span className={styles.pageNumber}>Page {currentPage} of {totalPages || 1}</span>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages || totalPages === 0}>›</button>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>»»</button>
                </div>
            </div>

            {showCreateModal && (
                <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Create New Role</h2>
                            <button className={styles.modalClose} onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Role Name </label>
                                    <input type="text" className={styles.input} required value={formData.roleName} onChange={(e) => setFormData({ ...formData, roleName: e.target.value })} placeholder="Enter role name" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Description</label>
                                    <textarea className={styles.textarea} rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter role description" />
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.cancelButton} onClick={() => setShowCreateModal(false)}>Cancel</button>
                                <button type="submit" className={styles.submitButton}>Create Role</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Edit Role</h2>
                            <button className={styles.modalClose} onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleEditSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Role Name </label>
                                    <input type="text" className={styles.input} required value={formData.roleName} onChange={(e) => setFormData({ ...formData, roleName: e.target.value })} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Description</label>
                                    <textarea className={styles.textarea} rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.cancelButton} onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="submit" className={styles.submitButton}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showHistoryModal && (
                <div className={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Role History</h2>
                            <button className={styles.modalClose} onClick={() => setShowHistoryModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            <p><strong>Role:</strong> {selectedRole?.name}</p>
                            <div className={styles.historyItem}>
                                <div className={styles.historyDate}>
                                    {selectedRole?.createdAt ? new Date(selectedRole.createdAt).toLocaleString() : 'N/A'}
                                </div>
                                <div className={styles.historyAction}>
                                    Role created {selectedRole?.createdByName && `by ${selectedRole.createdByName}`}
                                </div>
                            </div>
                            {selectedRole?.updatedAt && (
                                <div className={styles.historyItem}>
                                    <div className={styles.historyDate}>
                                        {new Date(selectedRole.updatedAt).toLocaleString()}
                                    </div>
                                    <div className={styles.historyAction}>
                                        Role updated {selectedRole?.updatedByName && `by ${selectedRole.updatedByName}`}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setShowHistoryModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                show={confirmModal.show}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ ...confirmModal, show: false }))}
            />
        </div>
    );
};

export default RoleManagement;